import { ChangeEvent, FC, ReactElement, useMemo, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QesColors } from "@app/shared/qes-standards/colors";
import { JobTypePerformanceStats, METRIC_OPTIONS, MetricKey, ProjectRunPerformanceResult } from "@app/operations/project-performance/types";

export interface JobTypePerformanceChartProps {
  data: ProjectRunPerformanceResult[];
  visibleJobTypes: string[];
}

interface JobTypeDataPoint {
  datePublished: string;
  projectRunConfigurationId: number;
  xKey: string;
  [jobType: string]: string | number | undefined;
}

const STATS_FIELD_BY_METRIC_KEY: Record<MetricKey, keyof JobTypePerformanceStats> = {
  AvgCpuPercent: "avgCpuPercent",
  AvgRamMb: "avgRamMb",
  AvgRuntimeSeconds: "avgRuntimeSeconds",
  MaxCpuPercent: "maxCpuPercent",
  MaxRamMb: "maxRamMb",
  MaxRuntimeMinutes: "maxRuntimeMinutes",
};

const LINE_COLORS = [
  QesColors.Blue500,
  QesColors.QesPrimaryRed,
  QesColors.QesTertiaryGreen2,
  QesColors.QesTertiaryOrange1,
  QesColors.QesTertiaryPurple4,
  QesColors.QesTertiaryYellow2,
  QesColors.QesPrimaryGreen,
  QesColors.QesTertiaryPurple1,
];

function colorForIndex(index: number): string {
  return LINE_COLORS[index % LINE_COLORS.length];
}

export function formatRunDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function toXKey(datePublished: string, projectRunConfigurationId: number): string {
  return `${datePublished}#${projectRunConfigurationId}`;
}

function formatXKeyTick(xKey: string): string {
  return formatRunDate(xKey.split("#")[0]);
}

function renderTooltipContent({ active, payload }: TooltipContentProps<number, string>): ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload as JobTypeDataPoint | undefined;

  return (
    <div className="job-type-performance-chart__tooltip bg-white border rounded p-2">
      <div>Run Configuration: {point?.projectRunConfigurationId}</div>
      <div>Date: {point?.datePublished ? formatRunDate(point.datePublished) : ""}</div>
      {payload.map(entry => (
        <div key={entry.name}>{entry.name}: {entry.value}</div>
      ))}
    </div>
  );
}

function toChartData(data: ProjectRunPerformanceResult[], visibleJobTypes: string[], metricKey: MetricKey): JobTypeDataPoint[] {
  const statsField = STATS_FIELD_BY_METRIC_KEY[metricKey];

  return data
    .filter(run => !!run.datePublished)
    .map(run => {
      const point: JobTypeDataPoint = {
        datePublished: run.datePublished as string,
        projectRunConfigurationId: run.projectRunConfigurationId,
        xKey: toXKey(run.datePublished as string, run.projectRunConfigurationId),
      };

      visibleJobTypes.forEach(jobType => {
        const stats = run.jobTypeBreakdown.find(jobTypeStats => jobTypeStats.jobType === jobType);
        const value = stats?.[statsField];
        if (value !== undefined && value !== null) {
          point[jobType] = value as number;
        }
      });

      return point;
    });
}

export const JobTypePerformanceChart: FC<JobTypePerformanceChartProps> = ({ data, visibleJobTypes }) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>(METRIC_OPTIONS[0].key);

  const allJobTypes = useMemo(
    () => Array.from(new Set(data.flatMap(run => run.jobTypeBreakdown.map(stats => stats.jobType)))),
    [data]
  );
  const chartData = useMemo(() => toChartData(data, visibleJobTypes, selectedMetric), [data, visibleJobTypes, selectedMetric]);
  const selectedOption = METRIC_OPTIONS.find(option => option.key === selectedMetric) ?? METRIC_OPTIONS[0];

  const handleMetricChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedMetric(event.target.value as MetricKey);
  };

  return (
    <div className="job-type-performance-chart">
      <div className="d-flex justify-content-end mb-2">
        <QESSelect
          label="Metric"
          onChange={handleMetricChange}
          testId="job-type-performance-chart__metric-select"
          value={selectedMetric}
        >
          {METRIC_OPTIONS.map(option => (
            <option key={option.key} value={option.key}>{`${option.label} (${option.unit})`}</option>
          ))}
        </QESSelect>
      </div>
      {chartData.length === 0 || visibleJobTypes.length === 0 ? (
        <p data-testid="job-type-performance-chart__empty">No performance data available for this project.</p>
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData} margin={{ bottom: 20, left: 20, right: 30, top: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="xKey" tickFormatter={formatXKeyTick} />
            <YAxis label={{ angle: -90, position: "left", style: { textAnchor: "middle" }, value: selectedOption.unit }} />
            <Legend />
            {visibleJobTypes.map(jobType => (
              <Line
                key={jobType}
                connectNulls={false}
                dataKey={jobType}
                isAnimationActive={false}
                name={jobType}
                stroke={colorForIndex(allJobTypes.indexOf(jobType))}
                type="monotone"
              />
            ))}
            <Tooltip content={(payload) => renderTooltipContent(payload)} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default JobTypePerformanceChart;
